import { useState } from 'react'
import { BaseModal, Button, Input, useToastActions } from '@airclou/ui'
import { CreditCard, ExternalLink } from 'lucide-react'
import { startCheckout } from '../../../services/billing'
import type { Plan } from '../../../services/plans'

/**
 * Collects the two billing details Stripe puts on the invoice, then hands the
 * customer to Checkout.
 *
 * Deliberately does not touch the tenant's name or contact email. Someone can
 * bill as "Acme Holdings Ltd" while working in a workspace called "Acme", and a
 * billing form is the wrong place to rename an organization for everyone else.
 */

export interface SubscribeModalProps {
  /** One paid tier of one suite. */
  plan: Plan
  /** Prefill for the seat count — the organization's member count is a good start. */
  defaultSeats?: number
  /** Prefill — the tenant's own name and the signed-in user's email. */
  defaultOrganizationName?: string
  defaultBillingEmail?: string
  /** True when the suite's subscription ended before: the wording becomes "subscribe again". */
  isReactivating?: boolean
  onClose: () => void
}

function formatPrice(priceCents: number): string {
  return `$${(priceCents / 100).toFixed(2).replace(/\.00$/, '')}`
}

/** A typo guard matching the server's; not a business limit. */
const MAX_SEATS = 1000

export function SubscribeModal({
  plan,
  defaultSeats = 1,
  defaultOrganizationName = '',
  defaultBillingEmail = '',
  isReactivating = false,
  onClose,
}: SubscribeModalProps) {
  const { error: toastError } = useToastActions()
  const [organizationName, setOrganizationName] = useState(defaultOrganizationName)
  const [billingEmail, setBillingEmail] = useState(defaultBillingEmail)
  // Held as text so the field can be cleared while typing; parsed for the total
  // and the request.
  const [seatsText, setSeatsText] = useState(String(Math.max(1, defaultSeats)))
  const seats = Number(seatsText)
  const seatsValid = Number.isInteger(seats) && seats >= 1 && seats <= MAX_SEATS
  const [isRedirecting, setIsRedirecting] = useState(false)

  const canSubmit =
    organizationName.trim().length > 0 && billingEmail.includes('@') && seatsValid && !isRedirecting

  async function handleSubmit() {
    setIsRedirecting(true)
    try {
      const url = await startCheckout({
        planSlug: plan.slug,
        seats,
        organizationName: organizationName.trim(),
        billingEmail: billingEmail.trim(),
      })
      // Full navigation, not a new tab: Checkout returns the browser to us, and
      // a popup would leave the original tab showing stale state forever.
      window.location.assign(url)
    } catch (err: any) {
      // Only reset on failure. On success the page is navigating away, and
      // flipping the button back to "Continue" would invite a second click that
      // starts a second checkout session.
      setIsRedirecting(false)
      // A plan with no Stripe price, or no Stripe at all, is our configuration
      // gap, not the customer's — the server's message names the plan slug and
      // the missing price, which is for the operator log, not a toast.
      const code = err?.code
      toastError(
        code === 'PLAN_NOT_PURCHASABLE' || code === 'BILLING_NOT_CONFIGURED'
          ? "Subscriptions aren't available yet. Please contact support."
          : err?.message || 'Could not start checkout'
      )
    }
  }

  return (
    // Shared BaseModal rather than a hand-rolled overlay, so this gets the
    // suite's focus trap, scroll lock and Escape handling for free. Both
    // dismissal paths are switched off once the browser is on its way to
    // Stripe: closing the dialog mid-redirect would leave the page looking
    // idle while a checkout session is already opening.
    <BaseModal
      isOpen
      onClose={onClose}
      closeOnBackdrop={!isRedirecting}
      closeOnEscape={!isRedirecting}
      disableCloseButton={isRedirecting}
      size="md"
      contentClassName="px-6 py-5"
      title={
        <span className="flex items-center gap-3">
          <CreditCard className="h-5 w-5 text-primary" />
          <span className="text-lg font-semibold">
            {isReactivating ? 'Subscribe again' : 'Subscribe'} — {plan.name}
          </span>
        </span>
      }
      footer={
        <div className="flex items-center justify-end gap-3">
          <Button variant="outline" onClick={onClose} disabled={isRedirecting}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={!canSubmit}>
            {isRedirecting ? (
              'Redirecting to Stripe...'
            ) : (
              <>
                Continue to payment
                <ExternalLink className="ml-2 h-4 w-4" />
              </>
            )}
          </Button>
        </div>
      }
    >
      <p className="mb-5 text-sm text-muted-foreground">
        {formatPrice(plan.priceCents)} per seat per month
        {seatsValid ? <> — <span className="font-medium text-foreground">{formatPrice(plan.priceCents * seats)} a month</span> for {seats} {seats === 1 ? 'seat' : 'seats'}</> : null}.
        The details below appear on your invoices; they don't change your organization or account settings.
      </p>

      <div className="space-y-4">
        <div>
          <label htmlFor="seats" className="mb-1.5 block text-sm font-medium text-foreground">
            Seats <span className="text-destructive">*</span>
          </label>
          <Input
            id="seats"
            type="number"
            min={1}
            max={MAX_SEATS}
            step={1}
            value={seatsText}
            onChange={(value: string) => setSeatsText(value)}
            disabled={isRedirecting}
          />
          <p className="mt-1 text-xs text-muted-foreground">One for each person who works tickets.</p>
        </div>

        <div>
          <label htmlFor="org-name" className="mb-1.5 block text-sm font-medium text-foreground">
            Organization name <span className="text-destructive">*</span>
          </label>
          <Input
            id="org-name"
            value={organizationName}
            onChange={(value: string) => setOrganizationName(value)}
            placeholder="The name to show on invoices"
            disabled={isRedirecting}
          />
        </div>

        <div>
          <label htmlFor="billing-email" className="mb-1.5 block text-sm font-medium text-foreground">
            Billing email <span className="text-destructive">*</span>
          </label>
          <Input
            id="billing-email"
            value={billingEmail}
            onChange={(value: string) => setBillingEmail(value)}
            placeholder="where receipts should go"
            disabled={isRedirecting}
          />
        </div>
      </div>
    </BaseModal>
  )
}
